import { useTranslation } from 'react-i18next';
import {
  initializeFlexibilityOptions,
  initializeCapacityOptions,
  getFixedFlexibilityCopies,
  getFixedCapacityAndFlexibilityCopies,
  isCapacityFixed,
  isFlexibilityFixed,
} from '~/components/pages/office/utils';
import { CoreOfficeDataType } from '~/types/offices';
import ValidatedSelectInput from './ValidatedSelectInput';
import FixedSelectDisplay from './FixedSelectDisplay';

interface SeatsAndFlexibilityProps {
  office: CoreOfficeDataType;
  requiredCapacity: string;
  daysPerWeek: string;
  setRequiredCapacity: (value: string) => void;
  setDaysPerWeek: (value: string) => void;
}

const SeatsAndFlexibility = ({
  office,
  requiredCapacity,
  daysPerWeek,
  setRequiredCapacity,
  setDaysPerWeek,
}: SeatsAndFlexibilityProps) => {
  const { t } = useTranslation();
  const flexibilityOptions = initializeFlexibilityOptions(office.flexibility, office.partTimeDays, t);
  const capacityOptions = initializeCapacityOptions(office.minCapacity, office.capacity, t);

  const capacityFixed = isCapacityFixed(office.minCapacity, office.capacity);
  const flexibilityFixed = isFlexibilityFixed(office.flexibility, office.partTimeDays);

  if (capacityFixed && flexibilityFixed) {
    const displayCopies = getFixedCapacityAndFlexibilityCopies(
      office.capacity,
      office.flexibility,
      office.partTimeDays,
      t,
    );
    return (
      <>
        <FixedSelectDisplay
          label={displayCopies.label}
          displayText={displayCopies.displayText}
          extraClasses="mt-4 mt-md-3"
        />
        <input
          type="hidden"
          name="requiredCapacity"
          value={requiredCapacity}
        />
        <input
          type="hidden"
          name="daysPerWeek"
          value={daysPerWeek}
        />
      </>
    );
  }

  return (
    <>
      {!capacityFixed && (
        <ValidatedSelectInput
          label={t('office.show.requestVisitCard.seats.label')}
          name="requiredCapacity"
          value={requiredCapacity}
          extraClasses="mt-4 mt-md-3"
          options={capacityOptions}
          optional={false}
          defaultValue={requiredCapacity}
          onChange={(e) => setRequiredCapacity(e.target.value)}
        />
      )}

      {!flexibilityFixed && (
        <ValidatedSelectInput
          label={t('office.show.requestVisitCard.flexibility.label')}
          name="daysPerWeek"
          value={daysPerWeek}
          extraClasses="mt-4 mt-md-3"
          options={flexibilityOptions}
          optional={false}
          defaultValue={daysPerWeek}
          onChange={(e) => setDaysPerWeek(e.target.value)}
        />
      )}

      {capacityFixed && (
        <>
          <FixedSelectDisplay
            label={t('office.show.requestVisitCard.fixedCapacity.label')}
            displayText={`${office.capacity} ${t('global.pluralization.i18next.seat', { count: office.capacity })}`}
            extraClasses="mt-4 mt-md-3"
          />
          <input
            type="hidden"
            name="requiredCapacity"
            value={requiredCapacity}
          />
        </>
      )}

      {flexibilityFixed && (
        <>
          <FixedSelectDisplay
            label={getFixedFlexibilityCopies(office.flexibility, office.partTimeDays, t).label}
            displayText={getFixedFlexibilityCopies(office.flexibility, office.partTimeDays, t).displayText}
            extraClasses="mt-4 mt-md-3"
          />
          <input
            type="hidden"
            name="daysPerWeek"
            value={daysPerWeek}
          />
        </>
      )}
    </>
  );
};

export default SeatsAndFlexibility;
